import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { isAxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { TableStore } from 'shared/ui/TableNew'
import { logger } from 'shared/lib'
import { type ITabItem } from 'shared/ui'
import { type PageLayoutStore } from 'shared/layout'
import { RequestLoadData } from 'shared/api/lib'
import type {
  IBroadcastType,
  IParamsGetBroadcasts,
  IResponseBroadcast,
  IResponseGetBroadcasts,
  IResponseOneTimeBroadcast,
  IResponseRecurringBroadcastById,
} from 'entities/Broadcast/api/types'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import {
  BroadcastOneTime,
  BroadcastRecurring,
  type IBroadcast,
  type IRowBroadcast,
} from 'entities/Broadcast'
import { BroadcastsPath, BroadcastsRoutes } from 'entities/ExportRouters'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { RowBroadcastOneTime } from 'entities/Broadcast/model/Table/RowBroadcastOneTime'
import { RowBroadcastRecurring } from 'entities/Broadcast/model/Table/RowBroadcastRecurring'
import { FiltersAndSearchStore } from 'widgets/FiltersAndSearch'

export type IBroadcastsListStoreProps = {
  type: IBroadcastType
  pageLayoutStore: PageLayoutStore
}

export class BroadcastsListStore {
  private readonly _tableStore: TableStore<IRowBroadcast>
  private readonly _filtersAndSearchStore: FiltersAndSearchStore
  private readonly _type: IBroadcastType
  private _search: string | null = null
  private _firstLoading = true
  private _itemsMap: Map<number, IBroadcast> = new Map()
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeActiveTab: IReactionDisposer | null = null
  private _hasRetriedAtMinLimit = false
  private _isRetrying = false
  private _activeTabKey: string = BroadcastsRoutes.sms
  private readonly _pageLayoutStore: PageLayoutStore

  constructor(
    { type, pageLayoutStore }: IBroadcastsListStoreProps,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
    this._entities.inboxesStore.fetchInboxes()

    this._type = type
    this._pageLayoutStore = pageLayoutStore
    this._tableStore = new TableStore({
      key: 'broadcasts_list',
      limit: 10,
      getLimit: () => tableLimitStore.getLimit('broadcasts-table'),
      setLimit: (value) => tableLimitStore.setLimit('broadcasts-table', value),
      saveColumns: this.saveColumns,
      getColumns: this.getColumns,
    })
    this._filtersAndSearchStore = new FiltersAndSearchStore(
      {
        getFilters: () => BroadcastApi.getFilters(type).then(({ data }) => data),
      },
      this._entities
    )

    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    this.reactionActiveTab()
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._disposeActiveTab?.()
    this._requestLoadData.dispose()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  get filtersAndSearchStore() {
    return this._filtersAndSearchStore
  }

  get loading() {
    return this._tableStore.loading
  }

  get tableStore() {
    return this._tableStore
  }

  get type() {
    return this._type
  }

  get isHideFilters() {
    return this.isEmptyState || this.isNoBroadcasts || this._firstLoading
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get itemsRows() {
    return this.items.map((item) => {
      if (item instanceof BroadcastOneTime) return new RowBroadcastOneTime(item)

      return new RowBroadcastRecurring(item, this._entities)
    })
  }

  get paramsWithoutPage(): Omit<IParamsGetBroadcasts, 'page'> {
    return {
      length: this._tableStore.limit || undefined,
      type: this._type,
      search: this._filtersAndSearchStore.params.search || undefined,
      filtersList: this._filtersAndSearchStore.params.filtersList,
      sortBy: this._tableStore.sortBy,
      sortOrder: this._tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IParamsGetBroadcasts {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get isEmptyState() {
    return this._entities.broadcastStore.isEmptyState
  }

  get isNoBroadcasts() {
    return !this.loading && !this._itemsMap.size && !this._filtersAndSearchStore.hasSearchParams
  }

  get isOneTimePage() {
    return this._type === 'rvm' || this._type === 'sms'
  }

  get activeTabKey() {
    return this._activeTabKey
  }

  get tabs(): ITabItem[] {
    return [
      {
        name: 'SMS',
        key: BroadcastsRoutes.sms,
      },
      {
        name: 'Ringless',
        key: BroadcastsRoutes.ringless,
      },
    ]
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  setActiveTabKey = (activeTabKey: string) => {
    this._activeTabKey = activeTabKey
  }

  handleSelectTab = (tab: ITabItem) => {
    this.setActiveTabKey(tab.key)
    uiStore.changeRouteViaVue({
      path: `/${BroadcastsPath.oneTime}/${tab.key}`,
    })
  }

  reactionActiveTab = () => {
    this._disposeActiveTab?.()
    this._disposeActiveTab = reaction(
      () => uiStore.pathName,
      (pathname) => {
        const lastSegment = pathname.split('/').pop()

        if (lastSegment === BroadcastsRoutes.sms || lastSegment === BroadcastsRoutes.ringless) {
          this._activeTabKey = lastSegment
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  saveColumns = async (columns: string[]): Promise<string[]> => {
    try {
      const data = await BroadcastApi.saveBroadcastsColumns({
        type: this._type,
        broadcast_fields: columns,
      })

      return data.data.data?.items || []
    } catch (e) {
      logger.error(e)
      return []
    }
  }
  getColumns = async (): Promise<string[]> => {
    try {
      const { data } = await BroadcastApi.getBroadcastsColumns(this._type)

      if (!data.data) return []

      return data.data.items
    } catch (e) {
      logger.error(e)

      return []
    }
  }

  private _requestLoadData = new RequestLoadData<boolean>({
    requestCallback: async ({ cancelRequest }, noLoading) => {
      runInAction(() => {
        this._tableStore.setLoading(!noLoading)
      })

      try {
        const { data } = await BroadcastApi.getBroadcasts(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })
        this.setData(data)
        runInAction(() => {
          this._hasRetriedAtMinLimit = false
          this._tableStore.setLoading(false)
        })
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: broadcasts-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this._tableStore.limit
          const newLimit = tableLimitStore.reduceLimit('broadcasts-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
              this._isRetrying = true
            })
            this._tableStore.setLimit(newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
              this._isRetrying = true
            })
            this._requestLoadData.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        if (!this._isRetrying) {
          this._tableStore.setLoading(false)
        }
        this._isRetrying = false
      })
    },
    finallyEndCallback: () => {
      runInAction(() => {
        this._firstLoading = false
      })
    },
  })

  loadData = async (noLoading?: boolean) => {
    return this._requestLoadData.loadData(noLoading)
  }

  setData = ({ data, meta }: IResponseGetBroadcasts) => {
    this._itemsMap.clear()
    this.setItems(data)
    this._tableStore.setTotal(meta.total)

    if (this._entities.broadcastStore.isEmptyState && data.length) {
      this._entities.broadcastStore.setIsEmptyState(false)
    } else if (
      this._entities.broadcastStore.loadingEmptyState &&
      uiStore.pathName.includes(BroadcastsRoutes.all)
    ) {
      this._entities.broadcastStore.setLoadingEmptyState(false)
    }
  }

  setItems = (items: IResponseBroadcast[]) => {
    items.forEach((item) => this.setItem(item))
  }

  setItem = (item: IResponseBroadcast) => {
    try {
      this._itemsMap.set(item.id, this._entities.broadcastStore.getBroadcastByResponse(item))
    } catch (e) {
      logger.error(e)
    }
  }

  onDelete = (ids: number[]) => {
    ids.forEach((id) => {
      this._itemsMap.delete(id)
    })
    this.loadData(true)
  }

  onUpdateItem = (item: IResponseOneTimeBroadcast | IResponseRecurringBroadcastById) => {
    try {
      const findBroadcast = this._itemsMap.get(item.id)
      if (findBroadcast) {
        if (findBroadcast instanceof BroadcastOneTime) {
          findBroadcast.syncOrigin(item as IResponseOneTimeBroadcast)
        }
        if (findBroadcast instanceof BroadcastRecurring) {
          findBroadcast.syncOrigin(item as IResponseRecurringBroadcastById)
        }
      } else if ('recurring_parent_id' in item && item.recurring_parent_id) {
        const findRecurringParentBroadcast = this._itemsMap.get(item.recurring_parent_id)
        if (findRecurringParentBroadcast instanceof BroadcastRecurring) {
          findRecurringParentBroadcast.handleUpdate(item)
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }
}
